import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiFragment, ApiRecordType, RecordTypesService, RecordTypeStructure } from '@stylehaven/components';
import { combineLatest, Observable, of } from 'rxjs';
import { map, mapTo, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import {
    SelectParentDialogOutputSuccess,
    SelectParentDialogType
} from '../../entities/dialog/select-parent-dialog.entity';
import { UploadHelper } from '../../sandboxes/upload/upload-helper.sandbox';
import { RecordsSandbox } from '../records/sandbox/records.sandbox.service';
import { DialogHelper } from './dialog.helper';

@Injectable({ providedIn: 'root' })
export class DialogFlowHelper {
    constructor(
        private readonly dialogHelper: DialogHelper,
        private readonly router: Router,
        private readonly uploadHelper: UploadHelper,
        private readonly recordTypeService: RecordTypesService,
        private readonly recordSandbox: RecordsSandbox
    ) {}

    /**
     * Starts the creation flow of a record as a child of another record.
     * @param parent A specific record to add the new record to. If undefined a selector popup is opened
     * @param files A list of files to upload. If undefined and a 'Data' record type is selected, a file selector popup is opened
     * @param navigateOnFinish Whether to navigate to the newly created record (or its parent) when uploading is finished
     * @returns
     */
    childFlow(config?: { parent?: ApiFragment; files?: FileList; navigateOnFinish?: boolean }): Observable<boolean> {
        const parentDialogOutput$: Observable<SelectParentDialogOutputSuccess> = config?.parent
            ? of({ type: 'success', parentRecord: config?.parent })
            : this.dialogHelper.selectParentDialog(SelectParentDialogType.UPLOAD);

        return parentDialogOutput$.pipe(
            map(parentOutput => parentOutput.parentRecord),
            switchMap(parent =>
                this.dialogHelper.recordTypeSelectionDialog(
                    parent?.getRecordType(),
                    // Only Data and DataFlat can be linked to files (other record types will be filtered out)
                    config?.files ? [RecordTypeStructure.Data, RecordTypeStructure.DataFlat] : undefined
                )
            ),
            withLatestFrom(parentDialogOutput$),
            switchMap(([recordTypeDialogOutput, parentOutput]) => {
                const recordType: ApiRecordType = recordTypeDialogOutput.recordType;

                switch (recordType.Structure) {
                    case RecordTypeStructure.Intellectual:
                        return this.executeChildIntellectualFlow(recordType, parentOutput.parentRecord);
                    case RecordTypeStructure.Data:
                    case RecordTypeStructure.DataFlat:
                        return this.executeChildDataFlow(recordType, parentOutput.parentRecord, {
                            inputFiles: config?.files,
                            navigateOnFinish: config?.navigateOnFinish ?? true
                        });
                    default:
                        return of(false);
                }
            })
        );
    }

    /**
     * Starts the flow of the creation of a record. Will branch into the child creation flow if
     * a recordtype is selected that can't be top-level.
     * @returns
     */
    allTypesFlow(): Observable<boolean> {
        const recordType$: Observable<ApiRecordType> = this.dialogHelper.recordTypeSelectionDialog().pipe(
            map(recordTypeOutput => recordTypeOutput.recordType),
            shareReplay(1)
        );

        const childStream$: Observable<[ApiFragment | undefined, ApiRecordType]> = recordType$.pipe(
            switchMap(recordType =>
                this.dialogHelper.selectParentDialog(SelectParentDialogType.UPLOAD, [recordType.Name])
            ),
            map(parentOutput => parentOutput.parentRecord),
            withLatestFrom(recordType$)
        );

        const topStream$: Observable<[ApiFragment | undefined, ApiRecordType]> = recordType$.pipe(
            map(recordType => [undefined, recordType])
        );

        return recordType$.pipe(
            switchMap(recordType =>
                this.recordTypeService.isTopLevelType(recordType.Name) ? topStream$ : childStream$
            ),
            switchMap(([parent, recordType]) => {
                switch (recordType.Structure) {
                    case RecordTypeStructure.Intellectual:
                        return !parent
                            ? this.executeTopLevelIntellectualFlow(recordType)
                            : this.executeChildIntellectualFlow(recordType, parent);
                    case RecordTypeStructure.Data:
                    case RecordTypeStructure.DataFlat:
                        return !parent
                            ? this.executeTopLevelDataFlow(recordType)
                            : this.executeChildDataFlow(recordType, parent);
                    default:
                        return of(false);
                }
            })
        );
    }

    private executeTopLevelIntellectualFlow(recordType: ApiRecordType): Observable<boolean> {
        return this.dialogHelper.zoneClassificationDialog().pipe(
            map(zoneClassificationOutput =>
                this.router.navigate(['/intellectual/new'], {
                    queryParams: {
                        recordType: recordType.Name,
                        zoneId: zoneClassificationOutput.zoneId,
                        parentId: zoneClassificationOutput.classificationId
                    }
                })
            ),
            mapTo(true)
        );
    }

    private executeTopLevelDataFlow(recordType: ApiRecordType): Observable<boolean> {
        return this.dialogHelper.zoneClassificationDialog().pipe(
            switchMap(zoneClassificationOutput =>
                this.uploadHelper.askUserForFiles().pipe(map(files => ({ zoneClassificationOutput, files })))
            ),
            switchMap(({ zoneClassificationOutput, files }) =>
                this.uploadHelper.uploadFiles(
                    files,
                    zoneClassificationOutput.classificationId,
                    zoneClassificationOutput.zoneId,
                    recordType
                )
            ),
            mapTo(true)
        );
    }

    private executeChildDataFlow(
        recordType: ApiRecordType,
        parent: ApiFragment,
        config?: { inputFiles?: FileList; navigateOnFinish: boolean }
    ): Observable<boolean> {
        const files$: Observable<FileList> = config?.inputFiles
            ? of(config.inputFiles)
            : this.uploadHelper.askUserForFiles();

        const zoneId = parent.getZoneId();
        const zoneId$ = zoneId
            ? of(zoneId)
            : this.dialogHelper.zoneClassificationDialog(parent.getClassificationId()).pipe(map(a => a.zoneId));

        return combineLatest([files$, zoneId$]).pipe(
            switchMap(([files, zoneId]) => this.recordSandbox.uploadChildRecords(parent, zoneId, recordType, files)),
            map(() => {
                if (config?.navigateOnFinish ?? true) {
                    this.router.navigate([parent.getContentUrl()]);
                }
            }),
            mapTo(true)
        );
    }

    private executeChildIntellectualFlow(recordType: ApiRecordType, parent: ApiFragment): Observable<boolean> {
        this.router.navigate(['/intellectual/new'], {
            queryParams: {
                recordType: recordType.Name,
                zoneId: parent.getZoneId(),
                parentId: parent.getMediaObjectId(),
                classificationId: parent.getClassificationId()
            },
            state: {
                parent
            }
        });

        return of(true);
    }
}
